import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import type { Block, MeetingSummaryBlock as MeetingSummaryBlockModel } from "@shared/types";
import { MeetingSummaryBlockView, meetingContext } from "../components/chat/blocks/MeetingSummaryBlock";
import {
  createMeeting, uploadSegment, stopMeeting, getMeeting, listMeetings, blobToBase64,
  importMeeting, retranscribeMeeting,
  daemonPairInit, daemonStatus, daemonRevoke,
  getDispatchPlan, dispatchMeeting, updateMeetingNotes, enhanceMeetingNotes, transcribePreview,
  type MeetingListItem, type MeetingSummaryData, type MeetingSegmentItem,
  type DispatchPlan, type DispatchOutcome, type DaemonDeviceView
} from "../lib/meeting-api";

import { MeetingGeneratingSkeleton } from "./MeetingGeneratingSkeleton";
import { fmtClock, speakerOrder, speakerColor, groupTurns } from "./transcript";
import { shouldTranscribe } from "./vad";
import { getMicStream, preferredMicId, setPreferredMicId } from "../lib/mic";

type DetailTab = "summary" | "notes" | "transcript";

type Mode = "home" | "recording" | "generating" | "detail";

export interface MeetingViewProps {
  /** Start a chat with a prompt (AI-native chips / ask-this-meeting). Closes the view. */
  onSendPrompt: (text: string) => void;
  /** MEETING_DISPATCH: show the Dispatch plan card + Dispatch all. */
  dispatchEnabled?: boolean;
  /** MEETING_NOTES (PH-4): tabbed detail (Summary/My notes/Transcript) + My notes editor. */
  notesEnabled?: boolean;
  /** MEETING_SKELETON (PH-5): five-part skeleton while generating (replaces spinner). */
  skeletonEnabled?: boolean;
  /** MEETING_LIVE (PH-6b): live transcript preview while recording. */
  liveEnabled?: boolean;
  /** MEETING_SESSION (PH-1): the meeting owns a real conversation (living context). */
  sessionEnabled?: boolean;
  /** PH-1: open the meeting's own conversation and ask a question there (context
   * already seeded), instead of stuffing the summary into a fresh chat. */
  onAskMeeting?: (conversationId: string, question: string) => void;
  /** MEETING_IMPORT (A4): import an existing audio file → transcribe → summary. */
  importEnabled?: boolean;
  /** MEETING_VAD (A1, §19): skip cloud ASR for silent preview windows / takes. */
  vadEnabled?: boolean;
  /** MEETING_SYSTEM_AUDIO (B1, meeting-daemon.md): 桌面采集 pairing card. */
  systemAudioEnabled?: boolean;
}

const pad = (n: number): string => String(n).padStart(2, "0");
const fmtElapsed = (ms: number): string => { const s = Math.floor(ms / 1000); return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`; };

function pickMime(): string {
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/wav"];
  for (const t of types) if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) return t;
  return "audio/webm";
}
const fmtFrom = (mime: string): "webm" | "mp4" | "wav" => (mime.includes("mp4") ? "mp4" : mime.includes("wav") ? "wav" : "webm");

/**
 * Meeting surface (uiux §2.2 Option A). Rendered in the main column; sidebar +
 * topbar stay. home → recording → generating → detail. AI-native: press-to-record
 * only (mic asked just-in-time), summary as a living block with next-steps + ask.
 */
export function MeetingView({ onSendPrompt, dispatchEnabled = false, notesEnabled = false, skeletonEnabled = false, liveEnabled = false, sessionEnabled = false, onAskMeeting, importEnabled = false, vadEnabled = false, systemAudioEnabled = false }: MeetingViewProps): JSX.Element {
  const [mode, setMode] = useState<Mode>("home");
  const [meetings, setMeetings] = useState<MeetingListItem[]>([]);
  const [meetingId, setMeetingId] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [ask, setAsk] = useState("");
  const [tab, setTab] = useState<DetailTab>("summary");
  const [notesDraft, setNotesDraft] = useState("");
  const [notesEnhanced, setNotesEnhanced] = useState<string | null>(null);
  const [notesBusy, setNotesBusy] = useState<null | "save" | "enhance">(null);
  const [notesMsg, setNotesMsg] = useState<string | null>(null);
  const [summary, setSummary] = useState<MeetingSummaryData | null>(null);
  const [segments, setSegments] = useState<MeetingSegmentItem[]>([]);
  const [elapsed, setElapsed] = useState(0);
  const [genElapsed, setGenElapsed] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [micLevel, setMicLevel] = useState(0);   // 0..1 real-time input level
  const [micSilent, setMicSilent] = useState(false); // no sound heard for a while
  // Input-device picker: continuity devices (a nearby iPhone) can be handed out
  // as the default while delivering near-silence — an explicit persisted choice
  // (web/src/lib/mic.ts) is the robust cure. activeMic = the label actually in
  // use while recording, shown next to the meter for diagnosability.
  const [micDevices, setMicDevices] = useState<{ id: string; label: string }[]>([]);
  const [micChoice, setMicChoice] = useState<string>(preferredMicId());
  const [activeMic, setActiveMic] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<DispatchPlan | null>(null);
  const [dispatchOutcome, setDispatchOutcome] = useState<DispatchOutcome | null>(null);
  const [dispatching, setDispatching] = useState(false);

  const loadPlan = useCallback((id: string) => {
    setPlan(null); setDispatchOutcome(null);
    if (!dispatchEnabled) return;
    getDispatchPlan(id).then(setPlan).catch(() => setPlan(null));
  }, [dispatchEnabled]);

  const doDispatch = useCallback(() => {
    if (meetingId === null) return;
    setDispatching(true);
    dispatchMeeting(meetingId)
      .then(setDispatchOutcome)
      .catch(() => setDispatchOutcome({ dispatched: false, reason: "派发失败，请重试", ids: [] }))
      .finally(() => setDispatching(false));
  }, [meetingId]);

  const saveNotes = useCallback(() => {
    if (meetingId === null) return;
    setNotesBusy("save"); setNotesMsg(null);
    updateMeetingNotes(meetingId, notesDraft)
      .then(() => setNotesMsg("已保存"))
      .catch(() => setNotesMsg("保存失败，请重试"))
      .finally(() => setNotesBusy(null));
  }, [meetingId, notesDraft]);

  // Enhance is NON-destructive (openspec §11: suggestion, not silent mutation):
  // it stages a preview; the user's editor text is untouched until they 采纳.
  const enhance = useCallback(() => {
    if (meetingId === null || notesDraft.trim().length === 0) return;
    setNotesBusy("enhance"); setNotesMsg(null); setNotesEnhanced(null);
    enhanceMeetingNotes(meetingId, notesDraft)
      .then((enhanced) => {
        if (enhanced.trim() === notesDraft.trim()) { setNotesMsg("已经很清晰了，没有可润色的改动"); return; }
        setNotesEnhanced(enhanced); setNotesMsg("下面是润色建议 — 采纳或放弃（你的原文未改动）");
      })
      .catch(() => setNotesMsg("润色失败，请重试"))
      .finally(() => setNotesBusy(null));
  }, [meetingId, notesDraft]);

  const acceptEnhanced = useCallback(() => {
    if (notesEnhanced === null) return;
    setNotesDraft(notesEnhanced); setNotesEnhanced(null); setNotesMsg("已采纳 — 记得点保存");
  }, [notesEnhanced]);

  const discardEnhanced = useCallback(() => { setNotesEnhanced(null); setNotesMsg(null); }, []);

  const recRef = useRef<MediaRecorder | null>(null);
  // §19 A1 VAD peaks (meter levels): per-preview-window + whole-take; meterOk
  // records that the meter actually ran (VAD fails open without it).
  const windowPeakRef = useRef(0);
  const takePeakRef = useRef(0);
  const meterOkRef = useRef(false);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeRef = useRef<string>("audio/webm");
  const startedRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previewRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previewBusyRef = useRef(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const meterRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshList = useCallback(() => { listMeetings().then(setMeetings).catch(() => setMeetings([])); }, []);
  useEffect(() => { if (mode === "home") refreshList(); }, [mode, refreshList]);
  // Auto-refresh the list on the home view so a meeting captured by the desktop
  // daemon (or one still transcribing) appears without a manual refresh.
  useEffect(() => {
    if (mode !== "home") return;
    const id = window.setInterval(refreshList, 5000);
    return () => window.clearInterval(id);
  }, [mode, refreshList]);

  // Enumerate audio inputs for the picker (labels are available once the site
  // has ever been granted mic permission; before that the picker just hides).
  useEffect(() => {
    if (mode !== "home") return;
    navigator.mediaDevices?.enumerateDevices?.()
      .then((ds) => setMicDevices(
        ds.filter((d) => d.kind === "audioinput" && d.deviceId !== "default" && d.deviceId !== "communications" && d.label.length > 0)
          .map((d) => ({ id: d.deviceId, label: d.label }))
      ))
      .catch(() => setMicDevices([]));
  }, [mode]);

  // Real elapsed counter for the generating skeleton (seconds since Stop).
  useEffect(() => {
    if (mode !== "generating") return;
    setGenElapsed(0);
    const t = setInterval(() => setGenElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [mode]);

  const cleanupStream = useCallback(() => {
    if (timerRef.current !== null) { clearInterval(timerRef.current); timerRef.current = null; }
    if (previewRef.current !== null) { clearInterval(previewRef.current); previewRef.current = null; }
    if (meterRef.current !== null) { clearInterval(meterRef.current); meterRef.current = null; }
    if (audioCtxRef.current !== null) { void audioCtxRef.current.close().catch(() => undefined); audioCtxRef.current = null; }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recRef.current = null;
    setMicLevel(0); setMicSilent(false);
  }, []);

  // Real-time mic level meter (Web Audio) — so the user SEES the app is actually
  // hearing them. If it stays near-silent for ~4s, warn (mic not captured).
  const startMeter = useCallback((stream: MediaStream) => {
    try {
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      let quietMs = 0;
      meterOkRef.current = true; // §19: VAD may gate only while the meter runs
      meterRef.current = setInterval(() => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) { const c = (v - 128) / 128; sum += c * c; }
        const rms = Math.sqrt(sum / buf.length); // 0..~1
        const level = Math.min(1, rms * 4);
        setMicLevel(level);
        // §19 VAD peaks: per-preview-window + whole-take
        if (level > windowPeakRef.current) windowPeakRef.current = level;
        if (level > takePeakRef.current) takePeakRef.current = level;
        if (level < 0.02) { quietMs += 200; if (quietMs >= 4000) setMicSilent(true); }
        else { quietMs = 0; setMicSilent(false); }
      }, 200);
    } catch { /* AudioContext unavailable — meter is best-effort; VAD fails open */ }
  }, []);

  // PH-6b live preview: transcribe the take SO FAR (chunks[0..k] — decodable
  // because it starts with the header) via the voice STT endpoint, without saving
  // a segment. Best-effort: skip if a previous preview is still in flight.
  const doPreview = useCallback(async () => {
    if (previewBusyRef.current || chunksRef.current.length === 0) return;
    // §19 A1 VAD: a preview window with no speech is not sent to cloud ASR
    // (the take itself is untouched — only this preview call is skipped).
    const windowPeak = windowPeakRef.current;
    windowPeakRef.current = 0;
    if (!shouldTranscribe(vadEnabled, meterOkRef.current, windowPeak)) return;
    previewBusyRef.current = true;
    try {
      const soFar = new Blob(chunksRef.current, { type: mimeRef.current });
      const text = await transcribePreview(await blobToBase64(soFar), fmtFrom(mimeRef.current));
      if (text.trim().length > 0) setLiveTranscript(text);
    } finally {
      previewBusyRef.current = false;
    }
  }, [vadEnabled]);
  useEffect(() => cleanupStream, [cleanupStream]);

  const startRecording = useCallback(async () => {
    setError(null);
    let stream: MediaStream;
    try {
      stream = await getMicStream(); // dead-continuity-default fallback (web/src/lib/mic.ts)
    } catch {
      setError("需要麦克风权限才能录音。请在浏览器允许，然后重试。");
      return;
    }
    const created = await createMeeting(title.trim().length > 0 ? title.trim() : undefined).catch(() => null);
    if (created === null) { setError("创建会议失败。"); stream.getTracks().forEach((t) => t.stop()); return; }
    setMeetingId(created.id);
    setNotesDraft(""); setNotesEnhanced(null); setNotesMsg(null); // fresh notes for this take
    setLiveTranscript("");
    mimeRef.current = pickMime();
    chunksRef.current = [];
    windowPeakRef.current = 0; takePeakRef.current = 0; meterOkRef.current = false;
    const rec = new MediaRecorder(stream, { mimeType: mimeRef.current });
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    streamRef.current = stream;
    recRef.current = rec;
    setActiveMic(stream.getAudioTracks()[0]?.label ?? "");
    startedRef.current = Date.now();
    setElapsed(0);
    setMicLevel(0); setMicSilent(false);
    startMeter(stream);
    timerRef.current = setInterval(() => setElapsed(Date.now() - startedRef.current), 1000);
    // MEETING_LIVE: chunk the recorder (timeslice) so a "take so far" blob is
    // available for the periodic live-transcript preview. The whole take is still
    // assembled from all chunks at stop (diarization/summary unchanged).
    if (liveEnabled) {
      rec.start(1000);
      previewRef.current = setInterval(() => { void doPreview(); }, 9000);
    } else {
      rec.start(); // whole-take
    }
    setMode("recording");
  }, [title, liveEnabled, doPreview, startMeter]);

  const stopRecording = useCallback(async () => {
    const rec = recRef.current;
    const id = meetingId;
    if (rec === null || id === null) return;
    const durMs = Date.now() - startedRef.current;
    setMode("generating");
    const blob: Blob = await new Promise((resolve) => {
      rec.onstop = () => resolve(new Blob(chunksRef.current, { type: mimeRef.current }));
      rec.stop();
    });
    cleanupStream();
    try {
      // §19 A1 VAD: a take that never contained speech is not sent to cloud ASR
      // at all — Stop still runs (an honestly-empty summary), audio never sent.
      if (shouldTranscribe(vadEnabled, meterOkRef.current, takePeakRef.current)) {
        const b64 = await blobToBase64(blob);
        await uploadSegment(id, { idx: 0, startMs: 0, endMs: durMs, audioBase64: b64, format: fmtFrom(mimeRef.current) });
      }
      const res = await stopMeeting(id);
      setSummary(res.summary);
      setConversationId(res.conversationId);
      // Persist any notes jotted DURING recording (边录边记) so they survive the
      // detail reload below (best-effort; needs MEETING_NOTES for the route).
      if (notesDraft.trim().length > 0) await updateMeetingNotes(id, notesDraft).catch(() => undefined);
      const detail = await getMeeting(id).catch(() => null);
      setSegments(detail?.segments ?? []);
      setNotesDraft(detail?.meeting.notes ?? notesDraft); setNotesEnhanced(null); setNotesMsg(null); setTab("summary");
      loadPlan(id);
      setMode("detail");
    } catch {
      setError("转写或生成纪要失败。片段已保存，可稍后重试。");
      setMode("home");
    }
  }, [meetingId, cleanupStream, loadPlan, notesDraft, vadEnabled]);

  // A4 (MEETING_IMPORT): pick an existing audio file → the same pipeline as
  // record→stop, no live capture. Uses the generating skeleton while the server
  // transcribes + summarizes, then lands on the detail page.
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const importFmt = (f: File): "webm" | "mp4" | "wav" | "mp3" | "ogg" => {
    const ext = f.name.toLowerCase().split(".").pop() ?? "";
    if (ext === "mp4" || ext === "m4a") return "mp4";
    if (ext === "wav" || ext === "mp3" || ext === "ogg" || ext === "webm") return ext;
    return "webm";
  };
  const onImportFile = useCallback(async (file: File) => {
    setError(null);
    setMode("generating");
    try {
      const audioBase64 = await blobToBase64(file);
      const res = await importMeeting({ title: file.name.replace(/\.[^.]+$/, ""), audioBase64, format: importFmt(file) });
      openMeetingRef.current?.(res.id);
    } catch {
      setError("导入失败——文件无法转写，请确认是有效的音频文件。");
      setMode("home");
    }
  }, []);

  // B1 桌面采集 (MEETING_SYSTEM_AUDIO): pairing card on home — issue a one-time
  // code for the desktop daemon, list paired devices, revoke (kill switch).
  const [daemonDevices, setDaemonDevices] = useState<DaemonDeviceView[]>([]);
  const [pairCode, setPairCode] = useState<{ code: string; expiresInSec: number } | null>(null);
  const refreshDaemon = useCallback(() => {
    if (!systemAudioEnabled) return;
    daemonStatus().then((s) => setDaemonDevices(s.devices)).catch(() => setDaemonDevices([]));
  }, [systemAudioEnabled]);
  useEffect(() => { if (mode === "home") refreshDaemon(); }, [mode, refreshDaemon]);
  const startPairing = useCallback(async () => {
    try {
      const issued = await daemonPairInit();
      setPairCode(issued);
      // One-click (openspec/meeting-daemon.md §14): try to wake the desktop app
      // via the cyclecare:// deep link with the code + this origin pre-filled, so
      // the user types nothing. If the app isn't installed the browser no-ops and
      // the code stays visible for manual entry.
      const link = `cyclecare://pair?code=${issued.code}&server=${encodeURIComponent(window.location.origin)}`;
      window.location.href = link;
    } catch { setError("生成配对码失败。"); }
  }, []);
  const openDesktopApp = useCallback(() => {
    if (pairCode === null) return;
    window.location.href = `cyclecare://pair?code=${pairCode.code}&server=${encodeURIComponent(window.location.origin)}`;
  }, [pairCode]);
  const revokeDevice = useCallback(async (id: string) => {
    try { await daemonRevoke(id); setDaemonDevices((ds) => ds.filter((d) => d.id !== id)); } catch { setError("解绑失败。"); }
  }, []);

  // §15 agentic capture routing (meeting-daemon.md): ONE record entry. When an
  // online desktop device can capture system audio, ask the scenario question
  // (线上会议 vs 现场) instead of making the user learn the difference between
  // the two capture paths; with no device the button records in-browser as
  // before, zero added friction.
  const [captureChoice, setCaptureChoice] = useState(false);
  const [desktopStarted, setDesktopStarted] = useState(false);
  const onlineDesktop = systemAudioEnabled && daemonDevices.some((d) => d.online);
  const onRecordPress = useCallback(() => {
    if (onlineDesktop) { setCaptureChoice(true); return; }
    void startRecording();
  }, [onlineDesktop, startRecording]);
  const startDesktopRecording = useCallback(() => {
    setCaptureChoice(false);
    setDesktopStarted(true);
    // Desktop starts capturing (system audio + mic); stop lives in its window.
    // The finished meeting lands in the list below via the 5s home polling.
    window.location.href = "cyclecare://record";
  }, []);

  // §20 (MEETING_IMPORT): re-upload this meeting's audio → re-run the pipeline
  // in place (e.g. after switching ASR model/endpoint). On failure the server
  // restores the old transcript, so we just land back on the unchanged detail.
  const retransInputRef = useRef<HTMLInputElement | null>(null);
  const onRetransFile = useCallback(async (file: File) => {
    const id = meetingId;
    if (id === null) return;
    setError(null);
    setMode("generating");
    try {
      const audioBase64 = await blobToBase64(file);
      await retranscribeMeeting(id, { audioBase64, format: importFmt(file) });
    } catch {
      setError("重新转写失败——原纪要与逐字稿未改动。");
    }
    openMeetingRef.current?.(id);
  }, [meetingId]);

  // openMeeting is declared below; a ref avoids a use-before-declare in the
  // import handler without reordering the existing callbacks.
  const openMeetingRef = useRef<((id: string) => void) | null>(null);

  const openMeeting = useCallback((id: string) => {
    getMeeting(id).then((d) => { setMeetingId(id); setConversationId(d.meeting.conversationId); setSummary(d.summary); setSegments(d.segments); setTitle(d.meeting.title ?? ""); setNotesDraft(d.meeting.notes ?? ""); setNotesEnhanced(null); setNotesMsg(null); setTab("summary"); loadPlan(id); setMode("detail"); }).catch(() => setError("打开会议失败。"));
  }, [loadPlan]);
  useEffect(() => { openMeetingRef.current = openMeeting; }, [openMeeting]);

  const summaryIsEmpty = summary !== null &&
    summary.exec.trim().length === 0 && summary.decisions.length === 0 &&
    summary.forYou.length === 0 && summary.actionsOthers.length === 0 && summary.risks.length === 0;

  // PH-1: when the meeting owns a real conversation (flag on + a conversationId),
  // "问这场会" continues THAT session — the summary is already seeded as context,
  // so we send only the question. Otherwise the legacy path stuffs the context.
  const sessionMode = sessionEnabled && conversationId !== null && onAskMeeting !== undefined;

  const submitAsk = useCallback((block: Block | null): void => {
    const q = ask.trim();
    if (q.length === 0) return;
    if (sessionMode && conversationId !== null && onAskMeeting !== undefined) {
      onAskMeeting(conversationId, q);
      setAsk("");
      return;
    }
    const ctx = block !== null && !summaryIsEmpty
      ? meetingContext(block as MeetingSummaryBlockModel)
      : `【会议「${title.trim().length > 0 ? title.trim() : "会议纪要"}」逐字稿】\n${segments.map((s) => `${s.speaker}：${s.text}`).join("\n")}`;
    onSendPrompt(`${ctx}\n\n基于以上这场会议，回答我的问题：${q}`);
    setAsk("");
  }, [ask, summaryIsEmpty, title, segments, onSendPrompt, sessionMode, conversationId, onAskMeeting]);

  // Chips route through the meeting session too when in session mode (context in
  // history → instruction only); else undefined so the block keeps stuffing context.
  const askInSession = sessionMode && conversationId !== null && onAskMeeting !== undefined
    ? (instruction: string): void => onAskMeeting(conversationId, instruction)
    : undefined;

  const summaryBlock: Block | null = summary === null ? null : {
    id: `ms-${meetingId ?? "x"}`, type: "meeting-summary", meetingId: meetingId ?? "",
    title: title.trim().length > 0 ? title.trim() : "会议纪要",
    exec: summary.exec, forYou: summary.forYou, decisions: summary.decisions,
    actionsOthers: summary.actionsOthers, risks: summary.risks, nextSteps: summary.nextSteps ?? []
  };

  // Detail sections — rendered as tab panels when MEETING_NOTES is on, stacked
  // when dark. Defined once so both layouts share the same content.
  const summarySection = (
    <>
      {summaryIsEmpty ? (
        <div className="mv-empty-note">这段录音里没有可提取的决策 / 待办 / 风险——录音与转写是成功的，逐字稿见 Transcript。</div>
      ) : summaryBlock !== null ? (
        <MeetingSummaryBlockView block={summaryBlock} onSendPrompt={onSendPrompt} onMutateStep={() => undefined} citations={[]} {...(askInSession !== undefined ? { askInSession } : {})} />
      ) : null}
      {dispatchEnabled && plan !== null && plan.phases.length > 0 && (
        <div className="mv-dispatch">
          <div className="mv-dispatch-head"><span className="mv-dispatch-h">Dispatch plan</span><span className="mv-dispatch-hint">小环的提议 · 可调整</span></div>
          <div className="mv-dispatch-card">
            <div className="mv-dispatch-meta">
              若派发 · {plan.itemCount} 项{plan.etaMinutes !== null ? ` · ETA ~${plan.etaMinutes}m` : ""}
              {plan.costEstimate !== null && <span className="mv-cost">{plan.costEstimate}</span>}
            </div>
            {plan.phases.map((ph, i) => (
              <div key={i} className="mv-phase">
                <div className="mv-phase-lab">{ph.label}<small>{ph.mode === "parallel" ? "parallel" : "serial"}</small></div>
                <div className="mv-phase-body">
                  {ph.assignments.map((a, k) => (
                    <div key={k} className="mv-assign">
                      <span className={`mv-role mv-role--${a.role}`} aria-hidden="true">{a.roleLabel.slice(0, 1)}</span>
                      <b className="mv-role-name">{a.roleLabel}</b>
                      {a.profile !== undefined
                        ? <span className="mv-assign-profile" title={`小环选用助手：${a.profile.label}`}>→ {a.profile.label}</span>
                        : <span className="mv-src">{a.source}</span>}
                      <span className="mv-assign-item">{a.item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="mv-dispatch-foot">
              {dispatchOutcome === null ? (
                <>
                  <span className="mv-dispatch-note">派发前会再次向你确认任何外部可见动作。</span>
                  <button type="button" className="mv-dispatch-btn" onClick={doDispatch} disabled={dispatching}>{dispatching ? "派发中…" : "⚡ Dispatch all"}</button>
                </>
              ) : dispatchOutcome.dispatched ? (
                <span className="mv-dispatch-ok">
                  ✓ 已派发 {dispatchOutcome.ids.length} 项任务给 agent 执行
                  {dispatchOutcome.feishu !== undefined && (dispatchOutcome.feishu.sent
                    ? <span className="mv-dispatch-feishu"> · 已同步飞书群</span>
                    : <span className="mv-dispatch-feishu mv-dispatch-feishu--off"> · 飞书未发（{dispatchOutcome.feishu.reason ?? "未配置"}）</span>)}
                </span>
              ) : (
                <span className="mv-dispatch-warn">{dispatchOutcome.reason}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  const trOrder = speakerOrder(segments);
  const transcriptSection = segments.length > 0 ? (
    <div className="mv-transcript">
      <div className="mv-transcript-h">逐字稿{trOrder.length > 1 ? ` · ${trOrder.length} 位发言人` : ""}</div>
      {groupTurns(segments).map((t, i) => (
        <div key={i} className="mv-tl-turn">
          <div className="mv-tl-meta">
            <span className="mv-tl-spk" style={{ color: speakerColor(t.speaker, trOrder) }}>
              <span className="mv-tl-dot" style={{ background: speakerColor(t.speaker, trOrder) }} aria-hidden="true" />{t.speaker}
            </span>
            <span className="mv-tl-ts">{fmtClock(t.startMs)}</span>
          </div>
          <div className="mv-tl-text">{t.text}</div>
        </div>
      ))}
    </div>
  ) : (
    <div className="mv-empty-note">暂无逐字稿。</div>
  );

  const notesSection = (
    <div className="mv-notes">
      <textarea
        className="mv-notes-editor"
        value={notesDraft}
        placeholder="记点什么——你自己的会议笔记。小环不会改动，除非你点“AI Enhance”并采纳。"
        onChange={(e) => { setNotesDraft(e.target.value); if (notesEnhanced !== null) setNotesEnhanced(null); }}
      />
      <div className="mv-notes-actions">
        <button type="button" className="mv-notes-enhance" onClick={enhance} disabled={notesBusy !== null || notesDraft.trim().length === 0}>
          {notesBusy === "enhance" ? "润色中…" : "✦ AI Enhance"}
        </button>
        <button type="button" className="mv-notes-save" onClick={saveNotes} disabled={notesBusy !== null}>
          {notesBusy === "save" ? "保存中…" : "保存"}
        </button>
        {notesMsg !== null && <span className="mv-notes-msg" role="status">{notesMsg}</span>}
      </div>
      {notesEnhanced !== null && (
        <div className="mv-notes-preview">
          <div className="mv-notes-preview-head"><span className="mv-notes-preview-title">✦ 润色建议</span><span className="mv-notes-preview-hint">你的原文未改动</span></div>
          <div className="mv-notes-preview-body">{notesEnhanced}</div>
          <div className="mv-notes-preview-actions">
            <button type="button" className="mv-notes-accept" onClick={acceptEnhanced}>采纳</button>
            <button type="button" className="mv-notes-discard" onClick={discardEnhanced}>放弃</button>
          </div>
        </div>
      )}
    </div>
  );

  // PH-6b live transcript preview (shown in the recording view when MEETING_LIVE).
  const liveBlock = liveEnabled ? (
    <div className="mv-live">
      <div className="mv-live-head"><span className="mv-live-badge">live</span>实时逐字稿</div>
      <div className="mv-live-body">{liveTranscript.length > 0 ? liveTranscript : "开始说话，几秒后这里出现实时转写…"}</div>
    </div>
  ) : null;

  // Mic level meter (always shown while recording) — so it's obvious the app is
  // hearing you; a persistent flat meter surfaces a mic that isn't captured.
  const micMeter = (
    <div className={`mv-mic ${micSilent ? "mv-mic--silent" : ""}`}>
      <span className="mv-mic-label">{micSilent ? "没检测到声音" : "在听你"}</span>
      <div className="mv-mic-bar"><div className="mv-mic-fill" style={{ width: `${Math.round(micLevel * 100)}%` }} /></div>
      {activeMic.length > 0 && <span className="mv-mic-device">{activeMic}</span>}
      {micSilent && <span className="mv-mic-hint">检查浏览器麦克风权限 / 输入设备是否选对——可回首页换一个麦克风再录</span>}
    </div>
  );

  return (
    <div className="meeting-view">
      {error !== null && <div className="mv-error" role="alert">{error}</div>}

      {mode === "home" && (
        <div className="mv-home">
          {captureChoice && (
            <div className="mv-hero mv-choose" role="group" aria-label="选择录制方式">
              <div className="mv-hero-title">这场会在哪儿？</div>
              <button type="button" className="mv-choose-btn" onClick={startDesktopRecording}>
                <span className="mv-choose-main">线上会议</span>
                <span className="mv-choose-sub">要录到对方的声音——用已连接的桌面端录（电脑里播放的声音 + 你的麦克风）</span>
              </button>
              <button type="button" className="mv-choose-btn" onClick={() => { setCaptureChoice(false); void startRecording(); }}>
                <span className="mv-choose-main">现场对话</span>
                <span className="mv-choose-sub">大家在同一个房间——只用这台设备的麦克风，在浏览器里直接录</span>
              </button>
              <button type="button" className="mv-choose-cancel" onClick={() => setCaptureChoice(false)}>取消</button>
            </div>
          )}
          {!captureChoice && (
          <div className="mv-hero">
            <button type="button" className="mv-rec-btn" aria-label="按一下就录" onClick={onRecordPress}>●</button>
            <div className="mv-hero-title">Capture a meeting. Get tasks back.</div>
            <div className="mv-hero-sub">录完一场会，小环还你摘要、可执行的决策、和可搜索的逐字稿。</div>
            <div className="mv-hero-hint">首次录音时才申请麦克风；连日历、提醒等用到时就地问——不用先设置。</div>
            {micDevices.length >= 2 && (
              <label className="mv-mic-select-row">
                麦克风
                <select
                  className="mv-mic-select"
                  value={micChoice}
                  onChange={(e) => { setMicChoice(e.target.value); setPreferredMicId(e.target.value); }}
                >
                  <option value="">系统默认</option>
                  {micDevices.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                </select>
              </label>
            )}
            {importEnabled && (
              <>
                <button type="button" className="mv-import-btn" onClick={() => importInputRef.current?.click()}>
                  导入音频文件
                </button>
                <input
                  ref={importInputRef}
                  type="file"
                  accept="audio/*,.webm,.mp4,.m4a,.wav,.mp3,.ogg"
                  className="mv-import-input"
                  style={{ display: "none" }}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f !== undefined) void onImportFile(f); }}
                />
              </>
            )}
          </div>
          )}
          {desktopStarted && (
            <div className="mv-desktop-live">
              <span className="mv-daemon-dot on" aria-hidden="true" />
              <span>已唤起桌面端开始录制——结束时在桌面小窗点「停止并生成纪要」，纪要会自动出现在下方列表。</span>
              <button type="button" className="mv-daemon-refresh" onClick={() => setDesktopStarted(false)}>知道了</button>
            </div>
          )}
          {systemAudioEnabled && (
            <div className="mv-daemon">
              <div className="mv-daemon-head">
                <span className="mv-daemon-title">桌面采集设备</span>
                <span className="mv-daemon-hint">管理已连接的桌面端——连上后录会议时可选「线上会议」录到对方的声音（系统音频）</span>
              </div>
              {daemonDevices.length > 0 && (
                <ul className="mv-daemon-list">
                  {daemonDevices.map((dev) => (
                    <li key={dev.id} className="mv-daemon-device">
                      <span className={`mv-daemon-dot ${dev.online ? "on" : ""}`} aria-hidden="true" />
                      <span className="mv-daemon-name">{dev.name}</span>
                      <span className="mv-daemon-state">{dev.online ? "在线" : "离线"}</span>
                      <button type="button" className="mv-daemon-revoke" onClick={() => void revokeDevice(dev.id)}>解绑</button>
                    </li>
                  ))}
                </ul>
              )}
              {pairCode !== null ? (
                <div className="mv-daemon-pair">
                  <div className="mv-daemon-pair-line">
                    配对码 <code className="mv-daemon-code">{pairCode.code}</code>
                    <button type="button" className="mv-daemon-open" onClick={openDesktopApp}>打开桌面端自动配对</button>
                  </div>
                  <span className="mv-daemon-pair-hint">
                    已尝试唤起桌面端并自动填入 · 若未安装可在桌面端手动输入此码，{Math.round(pairCode.expiresInSec / 60)} 分钟内有效 · 配对后
                    <button type="button" className="mv-daemon-refresh" onClick={() => { setPairCode(null); refreshDaemon(); }}>刷新状态</button>
                  </span>
                </div>
              ) : (
                <button type="button" className="mv-daemon-btn" onClick={() => void startPairing()}>连接桌面采集</button>
              )}
            </div>
          )}
          {meetings.length > 0 && (
            <ul className="mv-list">
              {meetings.map((m) => (
                <li key={m.id}>
                  <button type="button" className="mv-list-item" onClick={() => openMeeting(m.id)}>
                    <span className={`mv-dot mv-dot--${m.status}`} aria-hidden="true" />
                    <span className="mv-list-title">{m.title ?? "未命名会议"}</span>
                    <span className="mv-list-meta">{m.status}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {mode === "recording" && (
        notesEnabled ? (
          // 边录边记 (PH-6): jot My notes live while recording; auto-saved on Stop.
          <div className="mv-recording mv-recording--notes">
            <div className="mv-rec-bar">
              <div className="mv-rec-status"><span className="mv-rec-dot" aria-hidden="true" />REC <span className="mv-rec-time">{fmtElapsed(elapsed)}</span></div>
              <button type="button" className="mv-stop-btn" onClick={() => void stopRecording()}><span className="mv-stop-sq" aria-hidden="true" />Stop &amp; summarize</button>
            </div>
            {micMeter}
            {liveBlock}
            <div className="mv-rec-notes-head">My notes · 边录边记</div>
            {notesSection}
          </div>
        ) : (
          <div className="mv-recording">
            <div className="mv-rec-status"><span className="mv-rec-dot" aria-hidden="true" />REC <span className="mv-rec-time">{fmtElapsed(elapsed)}</span></div>
            {micMeter}
            <div className="mv-rec-hint">正在录音 · 结束后小环会生成纪要</div>
            <button type="button" className="mv-stop-btn" onClick={() => void stopRecording()}><span className="mv-stop-sq" aria-hidden="true" />Stop &amp; summarize</button>
            {liveBlock}
          </div>
        )
      )}

      {mode === "generating" && (
        skeletonEnabled
          ? <MeetingGeneratingSkeleton elapsedSec={genElapsed} />
          : <div className="mv-generating"><span className="mv-spin" aria-hidden="true" />小环正在生成纪要…</div>
      )}

      {mode === "detail" && summaryBlock !== null && (
        <div className="mv-detail">
          <div className="mv-detail-head">
            <div className="mv-detail-eyebrow">Meeting · captured by Cycle</div>
            <div className="mv-detail-title">{title.trim().length > 0 ? title.trim() : "会议纪要"}</div>
            {importEnabled && (
              <>
                <button
                  type="button"
                  className="mv-retrans-btn"
                  title="换了转写模型或自托管端点后，重新上传这场会的音频原地重跑转写与纪要"
                  onClick={() => retransInputRef.current?.click()}
                >
                  重新转写
                </button>
                <input
                  ref={retransInputRef}
                  type="file"
                  accept="audio/*,.webm,.mp4,.m4a,.wav,.mp3,.ogg"
                  className="mv-retrans-input"
                  style={{ display: "none" }}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f !== undefined) void onRetransFile(f); }}
                />
              </>
            )}
          </div>

          {notesEnabled ? (
            <>
              <div className="mv-tabs" role="tablist" aria-label="会议详情">
                <button type="button" role="tab" aria-selected={tab === "summary"} className={`mv-tab ${tab === "summary" ? "on" : ""}`} onClick={() => setTab("summary")}>Summary</button>
                <button type="button" role="tab" aria-selected={tab === "notes"} className={`mv-tab ${tab === "notes" ? "on" : ""}`} onClick={() => setTab("notes")}>My notes</button>
                <button type="button" role="tab" aria-selected={tab === "transcript"} className={`mv-tab ${tab === "transcript" ? "on" : ""}`} onClick={() => setTab("transcript")}>Transcript <span className="mv-tab-ct">{segments.length}</span></button>
              </div>
              <div className="mv-tabpanel" role="tabpanel">
                {tab === "summary" && summarySection}
                {tab === "notes" && notesSection}
                {tab === "transcript" && transcriptSection}
              </div>
            </>
          ) : (
            <>
              {summarySection}
              {segments.length > 0 && transcriptSection}
            </>
          )}

          <div className="mv-ask">
            <input
              className="mv-ask-input"
              type="text"
              value={ask}
              placeholder="问这场会任何事——“我答应了什么？”"
              onChange={(e) => setAsk(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitAsk(summaryBlock); }}
            />
            <button type="button" className="mv-ask-send" aria-label="发送" onClick={() => submitAsk(summaryBlock)}>↑</button>
          </div>
          <button type="button" className="mv-back" onClick={() => setMode("home")}>← 返回会议</button>
        </div>
      )}
    </div>
  );
}
